import { DateTime } from 'luxon';

/**
 * t294u: the shared date range presets (components/ui/DateRangeFilter). Every range is a pair of inclusive UK days
 * (Europe/London), weeks start on Monday, and "This week" / "This month" are the whole calendar week or month. In the
 * URL a preset is its id (?range=last-7-days) and a custom range is "from..to" (?range=2026-10-01..2026-10-14).
 */
export const LONDON = 'Europe/London';

/** t329u: 'tomorrow' and 'overdue' are for My checklists only (not in ALL_PRESETS); 'overdue' has no fixed days. */
export type RangePreset = 'any' | 'today' | 'tomorrow' | 'overdue' | 'yesterday' | 'last-7-days' | 'last-30-days' | 'this-week' | 'last-week' | 'this-month' | 'last-month' | 'custom';
export const PRESET_LABELS: Record<RangePreset, string> = {
  any: 'Any time', today: 'Today', tomorrow: 'Tomorrow', overdue: 'Overdue', yesterday: 'Yesterday', 'last-7-days': 'Last 7 days', 'last-30-days': 'Last 30 days',
  'this-week': 'This week', 'last-week': 'Last week', 'this-month': 'This month', 'last-month': 'Last month', custom: 'Custom range',
};
/** The usual menu, in order (custom last). Screens pass their own subset. */
export const ALL_PRESETS: RangePreset[] = ['today', 'yesterday', 'last-7-days', 'last-30-days', 'this-week', 'last-week', 'this-month', 'last-month', 'custom'];

/** A chosen range: the preset and the inclusive UK days it covers ('' for both when it is "Any time"). */
export interface DateRangeValue { preset: RangePreset; from: string; to: string }

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
export const isIsoDay = (v: string | null | undefined): v is string => Boolean(v && ISO_DAY.test(v) && DateTime.fromISO(v, { zone: LONDON }).isValid);
const londonNow = (now?: DateTime | Date) => (now instanceof Date ? DateTime.fromJSDate(now) : now ?? DateTime.now()).setZone(LONDON);
const day = (d: DateTime) => d.toISODate()!;

/** The inclusive UK days for a preset at `now` (null for "Any time" and "Custom range", which have no fixed days). */
export function presetDays(preset: RangePreset, now?: DateTime | Date): { from: string; to: string } | null {
  // Luxon weeks are ISO weeks, so startOf('week') is Monday and endOf('week') Sunday.
  const t = londonNow(now).startOf('day');
  switch (preset) {
    case 'today': return { from: day(t), to: day(t) };
    case 'tomorrow': { const n = t.plus({ days: 1 }); return { from: day(n), to: day(n) }; }
    case 'yesterday': { const y = t.minus({ days: 1 }); return { from: day(y), to: day(y) }; }
    case 'last-7-days': return { from: day(t.minus({ days: 6 })), to: day(t) };
    case 'last-30-days': return { from: day(t.minus({ days: 29 })), to: day(t) };
    case 'this-week': return { from: day(t.startOf('week')), to: day(t.endOf('week')) };
    case 'last-week': { const w = t.minus({ weeks: 1 }); return { from: day(w.startOf('week')), to: day(w.endOf('week')) }; }
    case 'this-month': return { from: day(t.startOf('month')), to: day(t.endOf('month')) };
    case 'last-month': { const m = t.minus({ months: 1 }); return { from: day(m.startOf('month')), to: day(m.endOf('month')) }; }
    default: return null;
  }
}

/** A preset as a value (its days filled in). */
export function presetValue(preset: Exclude<RangePreset, 'custom'>, now?: DateTime | Date): DateRangeValue {
  const d = presetDays(preset, now);
  return { preset, from: d?.from ?? '', to: d?.to ?? '' };
}

/** Days → value: the first preset in `presets` covering exactly those days, else a custom range. Backwards days are swapped. */
export function valueForDays(from: string, to: string, presets: RangePreset[] = ALL_PRESETS, now?: DateTime | Date): DateRangeValue {
  if (!from && !to && presets.includes('any')) return { preset: 'any', from: '', to: '' };
  const [a, b] = from && to && from > to ? [to, from] : [from, to];
  for (const p of presets) {
    const d = presetDays(p, now);
    if (d && d.from === a && d.to === b) return { preset: p, from: a, to: b };
  }
  return { preset: 'custom', from: a, to: b };
}

/** ?range= → value. Unknown or not-offered presets fall back to the screen's default. Rolling presets are worked out at `now`. */
export function parseRange(raw: string | null | undefined, presets: RangePreset[], fallback: Exclude<RangePreset, 'custom'>, now?: DateTime | Date): DateRangeValue {
  if (raw && raw.includes('..')) {
    const [a, b] = raw.split('..');
    if (isIsoDay(a) && isIsoDay(b)) return a > b ? { preset: 'custom', from: b, to: a } : { preset: 'custom', from: a, to: b };
  }
  if (raw && raw !== 'custom' && (presets.includes(raw as RangePreset) || raw === fallback)) return presetValue(raw as Exclude<RangePreset, 'custom'>, now);
  return presetValue(fallback, now);
}

/** Value → ?range= text: the preset id, or "from..to" for a custom range. */
export const formatRangeParam = (v: DateRangeValue) => (v.preset === 'custom' ? `${v.from}..${v.to}` : v.preset);

/** The button text: the preset's name, or the custom days en-GB ("1 Oct – 14 Oct", "Tue 14 Oct", years only when not this year). */
export function rangeLabel(v: DateRangeValue, now?: DateTime | Date, labels?: Partial<Record<RangePreset, string>>): string {
  if (v.preset !== 'custom') return labels?.[v.preset] ?? PRESET_LABELS[v.preset];
  if (!isIsoDay(v.from) || !isIsoDay(v.to)) return PRESET_LABELS.custom;
  const year = londonNow(now).year;
  const a = DateTime.fromISO(v.from, { zone: LONDON }).setLocale('en-GB');
  const b = DateTime.fromISO(v.to, { zone: LONDON }).setLocale('en-GB');
  const f = (d: DateTime) => d.toFormat(a.year === year && b.year === year ? 'd LLL' : 'd LLL yyyy');
  if (v.from === v.to) return a.toFormat(a.year === year ? 'ccc d LLL' : 'ccc d LLL yyyy');
  return `${f(a)} – ${f(b)}`;
}

/**
 * The UTC instants a range covers on the London clock: start of the first day and start of the day after the last
 * (exclusive), so a range over the clocks changing still covers whole UK days (23 or 25 hours on those days).
 */
export function londonBounds(v: Pick<DateRangeValue, 'from' | 'to'>): { start: string; end: string } | null {
  if (!isIsoDay(v.from) || !isIsoDay(v.to)) return null;
  return {
    start: DateTime.fromISO(v.from, { zone: LONDON }).startOf('day').toUTC().toISO()!,
    end: DateTime.fromISO(v.to, { zone: LONDON }).plus({ days: 1 }).startOf('day').toUTC().toISO()!,
  };
}

/** The UK day an instant falls on (for showing a timestamp range from a link as days). */
export const londonDay = (iso: string) => DateTime.fromISO(iso, { zone: 'utc' }).setZone(LONDON).toISODate() ?? '';
